import { faX } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { isEqual } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  NotesBlockWrapper,
  NotesBlockContainer,
  ChatContainer,
  NotesTextfieldContainer,
  NotesMessagesContainer,
  MessageContainer,
  RequestContainer,
} from "./styles";
import SuggestedNotes from "./SuggestedNotes/SuggestedNotes";
import { AttachIcon, SendIcon } from "../../../../../../../../../assets/icons";
import AddNewDocument from "../../../../../../../../../containers/AddNewDocument/AddNewDocument";
import AttachedDocuments from "../../../../../../../../../containers/AttachedDocuments/AttachedDocuments";
import LoadingOverlay from "../../../../../../../../../containers/LoadingOverlay/LoadingOverlay";
import { useAppSelector } from "../../../../../../../../../custom-hooks/redux/redux";
import { isAuthorizedEeGroup } from "../../../../../../../../../custom-hooks/useAuthorization/useAuthorization";
import { Page } from "../../../../../../../../../models/api/page";
import { PropsFilter } from "../../../../../../../../../models/api/props-filter";
import { CompanyLegalEntityRef } from "../../../../../../../../../models/company-accounts/company-legal-entities";
import { DocumentCategoryRef } from "../../../../../../../../../models/document-categories";
import { DocumentTypeRef } from "../../../../../../../../../models/document-types";
import { DocumentDTO, DocumentRef } from "../../../../../../../../../models/documents";
import { EmployeeRef } from "../../../../../../../../../models/employees";
import {
  TravelExpenseAuditNotesDTO,
  TravelExpenseAuditNotesFilter,
  TravelExpenseAuditNotesSTO,
} from "../../../../../../../../../models/travel-expense-audit-notes";
import { TravelExpenseAuditNoteByLinkFilter } from "../../../../../../../../../models/travel-expense-audit-notes/travel-expense-audit-note-by-link-filter";
import { TravelExpenseAuditNotesRef } from "../../../../../../../../../models/travel-expense-audit-notes/travel-expense-audit-notes-ref";
import { TravelExpenseAuditNoteReviewSTO } from "../../../../../../../../../models/travel-expense-note-preview";
import { TravelExpenseSuggestedNoteRef } from "../../../../../../../../../models/travel-expense-suggested-note";
import { TravelExpenseRef } from "../../../../../../../../../models/travel-expenses";
import { TravelExpenseAuditNoteReviewApiService } from "../../../../../../../../../services/travel-expense-audit-note-review/travel-expense-audit-note-peview-api.service";
import { TravelExpenseAuditNotesApiService } from "../../../../../../../../../services/travel-expense-audit-notes/travel-expense-audit-notes-api.service";
import { TravelExpensesApiService } from "../../../../../../../../../services/travel-expenses/travel-expenses-api.service";
import { AUTHORIZATIONS } from "../../../../../../../../../utils/constants/authorization-constants";
import { DOCUMENT_CATEGORIES, DOCUMENT_TYPES } from "../../../../../../../../../utils/constants/constants";
import { intlFormatDateShort, intlFormatDateToHour } from "../../../../../../../../../utils/time-utils";
import { toastifyResponse } from "../../../../../../../../../utils/toastify-response/toastify-response";

interface Props {
  employee: EmployeeRef;
  travelExpense: TravelExpenseRef;
  legalEntity: CompanyLegalEntityRef | null;
  readOnly: boolean;
  updateLastAuditNote?: (auditNote: TravelExpenseAuditNotesDTO) => void;
  textInsteadChipTitle?: boolean;
  isManagedByLink?: boolean;
  handleUpdateExportAuditNotes?: (travelExpenseId: string, include: boolean) => void;
}

interface State {
  loading: boolean;
  message: string;
  allMessages: TravelExpenseAuditNotesDTO[];
  page: Page;
  error: string;
  reload: boolean;
  totalItems: number;
  auditDocument: DocumentRef | null;
  loadingSaveExportNotes: boolean;
  includeNotesInExportedReport: boolean | null;
  basedOn: TravelExpenseSuggestedNoteRef | null;
}

const AuditNotes = ({
  employee,
  travelExpense,
  legalEntity,
  readOnly,
  updateLastAuditNote,
  textInsteadChipTitle = false,
  isManagedByLink = false,
  handleUpdateExportAuditNotes,
}: Props) => {
  const theme = useTheme();
  const tRP = "components.add_edit_travel_expense.fields";
  const { t } = useTranslation();

  const currCompanyAccount = useAppSelector((state) => state.currentUserReducer.currentCompanyAccount);
  const { currentUserData, currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);
  const [searchParams] = useSearchParams();

  const userId = searchParams.get("u");

  const isAuditor = useMemo(() => {
    return isAuthorizedEeGroup(
      [AUTHORIZATIONS.EmployeesAccess.AuditTravelAndExpenses],
      currentUserAuthorizations,
      employee?.groups?.map((group) => group?.id) ?? [],
      true
    );
  }, [currentUserAuthorizations, employee?.groups]);

  const [state, setState] = useState<State>({
    loading: false,
    message: "",
    allMessages: [],
    page: {
      pageNumber: 0,
      pageSize: 10,
    },
    totalItems: 0,
    error: "",
    reload: false,
    auditDocument: null,
    loadingSaveExportNotes: false,
    includeNotesInExportedReport: travelExpense.includeNotesInExportedReport,
    basedOn: null,
  });

  const includeNotesInExportedReportRef = useRef(state.includeNotesInExportedReport);

  const loadNotes = useCallback(
    async (signal?: AbortSignal, isFirstLoading?: boolean) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const page = {
          pageNumber: state.page.pageNumber,
          pageSize: isFirstLoading ? state.page.pageSize : state.page.pageSize + 10,
        };

        const apiService = new TravelExpenseAuditNotesApiService();
        let propsFilter: PropsFilter = {};

        if (isManagedByLink) {
          apiService.useLoadItemsPath("/GetTravelExpenseAuditNoteByLink");
          propsFilter = {
            travelExpenseId: travelExpense.id,
            userId,
          } as TravelExpenseAuditNoteByLinkFilter;
        } else {
          propsFilter = {
            travelExpenseIds: [travelExpense.id],
          } as TravelExpenseAuditNotesFilter;
        }

        const response = await apiService.loadItems(propsFilter, undefined, undefined, page, undefined, 60, signal);

        if (!signal?.aborted) {
          if (response.error) {
            setState((state) => ({ ...state, error: response.error }));
          } else {
            setState((state) => ({
              ...state,
              allMessages: response.items,
              totalItems: response.totalItems,
              error: "",
              page,
            }));
          }
        }

        toastifyResponse(response, "failed");
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({ ...state, loading: false, reload: false }));
      }
    },
    [travelExpense]
  );

  useEffect(() => {
    const abortController = new AbortController();

    travelExpense && loadNotes(abortController.signal, true);

    return () => abortController.abort();
  }, [travelExpense]);

  const saveRequest = useCallback(async () => {
    setState((state) => ({
      ...state,
      loading: true,
    }));

    try {
      const response = await new TravelExpenseAuditNotesApiService().saveItems([
        {
          id: uuidv4(),
          notes: state.message,
          companyAccount: currCompanyAccount,
          legalEntity,
          travelExpense,
          auditDocument: state.auditDocument,
          basedOn: state.basedOn,
        } as TravelExpenseAuditNotesSTO,
      ]);

      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error as string,
          loading: false,
        }));
      } else {
        setState((state) => ({
          ...state,
          reload: true,
          message: "",
          loading: false,
          auditDocument: null,
        }));
      }

      toastifyResponse(response);
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
        loading: false,
      }));
    }
  }, [travelExpense, currCompanyAccount, legalEntity, state.message, state.auditDocument, state.basedOn]);

  useEffect(() => {
    const abortController = new AbortController();

    state.reload && loadNotes(abortController.signal, true);

    return () => {
      abortController.abort();
    };
  }, [state.reload]);

  const handleUpdateIncludeExportAuditNotes = useCallback(async () => {
    setState((state) => ({ ...state, loadingSaveExportNotes: true }));
    if (travelExpense.includeNotesInExportedReport !== null) {
      try {
        const response = await new TravelExpensesApiService().saveTravelExpenseAuditDetails([
          {
            ...travelExpense,
            includeNotesInExportedReport: state.includeNotesInExportedReport as boolean,
          },
        ]);
        // if(!response.error && handleUpdateExportAuditNotes) handleUpdateExportAuditNotes(travelExpense.id , state.includeNotesInExportedReport ?? false)
      } catch (error) {
        console.error(error as string);
      } finally {
        setState((state) => ({ ...state, loadingSaveExportNotes: false }));
      }
    }
  }, [travelExpense, state.includeNotesInExportedReport, handleUpdateExportAuditNotes]);

  useEffect(() => {
    if (state.includeNotesInExportedReport !== travelExpense.includeNotesInExportedReport) {
      handleUpdateIncludeExportAuditNotes();
      includeNotesInExportedReportRef.current = state.includeNotesInExportedReport;
    }
  }, [state.includeNotesInExportedReport]);

  useEffect(() => {
    return () => {
      setState((state) => ({
        ...state,
        loading: false,
        message: "",
        allMessages: [],
        page: {
          pageNumber: 0,
          pageSize: 10,
        },
        error: "",
        reload: false,
      }));
    };
  }, []);

  useEffect(() => {
    const lastTravelExpenseNote = travelExpense.auditNotes[0];
    const lastActualNote = state.allMessages[0];

    if (
      !!updateLastAuditNote &&
      !!lastTravelExpenseNote &&
      !!lastTravelExpenseNote &&
      !isEqual(lastTravelExpenseNote?.id, lastActualNote?.id)
    ) {
      updateLastAuditNote(lastActualNote);
    }
  }, [updateLastAuditNote, state.allMessages, travelExpense]);

  const handleMArkAuditNoteAsReviewed = useCallback(async () => {
    const unreadMessages = state.allMessages.filter(
      (message) =>
        !message.travelExpenseAuditNoteReviews.find(
          (review) => review.createdByUser?.id !== currentUserData?.user?.id
        ) && message.createdByUser?.id !== currentUserData?.user?.id
    );

    if (unreadMessages.length > 0) {
      try {
        const response = await new TravelExpenseAuditNoteReviewApiService().saveItems([
          ...unreadMessages.map(
            (unreadMessage) =>
              ({
                id: uuidv4(),
                travelExpenseAuditNote: unreadMessage as TravelExpenseAuditNotesRef,
              } as TravelExpenseAuditNoteReviewSTO)
          ),
        ]);
        if (response.error) {
          console.error(response.error);
        }
      } catch (error) {
        console.error(error);
      }
    }
  }, [currentUserData?.user?.id, state.allMessages]);

  useEffect(() => {
    handleMArkAuditNoteAsReviewed();
  }, [state.allMessages]);

  useEffect(() => {
    return () => {
      handleUpdateExportAuditNotes &&
        handleUpdateExportAuditNotes(travelExpense.id, includeNotesInExportedReportRef.current ?? false);
    };
  }, []);

  const pickSuggestedNote = useCallback((suggestedNote: TravelExpenseSuggestedNoteRef) => {
    setState((state) => ({
      ...state,
      message: suggestedNote.note,
      basedOn: suggestedNote,
    }));
  }, []);

  const latestSuggestedNote = useMemo(() => {
    return state.allMessages.filter((_note) => !!_note.basedOn).flatMap((_note) => _note.basedOn)[0] ?? undefined;
  }, [state.allMessages]);

  return (
    <Grid>
      <LoadingOverlay loading={state.loading}>
        <Grid
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: textInsteadChipTitle ? "10px" : "15px",
          }}
        >
          <Grid>
            {!!textInsteadChipTitle && (
              <Typography sx={{ fontWeight: "bold" }}>{`${t(`${tRP}.audit_notes`)}`}: </Typography>
            )}

            {!textInsteadChipTitle && (
              <Divider>
                <Chip
                  label={`${t(`${tRP}.audit_notes`)}`}
                  size="small"
                  style={{ color: theme.palette.secondary.light }}
                />
              </Divider>
            )}
          </Grid>

          <Grid>
            <NotesBlockWrapper>
              <NotesBlockContainer>
                {isAuditor && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.includeNotesInExportedReport ?? false}
                        onChange={(_, checked: boolean) => {
                          setState((state) => ({ ...state, includeNotesInExportedReport: checked }));
                        }}
                      />
                    }
                    label={`${t(`${tRP}.include_in_exported_report`)}`}
                  />
                )}
                {!readOnly && (
                  <NotesTextfieldContainer>
                    <TextField
                      label={`${t(`${tRP}.add_note`)}`}
                      style={{ width: "100%" }}
                      value={state.message}
                      onChange={(event) => {
                        const message = event.currentTarget?.value;

                        setState((state) => ({
                          ...state,
                          message,
                        }));
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();

                          saveRequest();
                        }
                      }}
                      disabled={state.loading || readOnly}
                      InputProps={{
                        startAdornment: (
                          <Grid sx={{ display: "flex", flexDirection: "row", gap: "5px", paddingRight: "5px" }}>
                            <>
                              <SuggestedNotes
                                handleChangeSuggestedNote={pickSuggestedNote}
                                latestSuggestedNote={latestSuggestedNote}
                                travelExpenseTypeId={travelExpense.type?.id}
                              />

                              {!!state.auditDocument && (
                                <Grid style={{ display: "flex" }}>
                                  <AttachedDocuments
                                    documentIDs={[state.auditDocument?.id ?? ""]}
                                    count={state.auditDocument?.filesCount ?? 0}
                                    buttonSize="2x"
                                    useSimpleView
                                    allowShare={false}
                                  />
                                  <IconButton
                                    onClick={() => {
                                      setState((state) => ({ ...state, auditDocument: null }));
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faX}
                                      color={theme.palette.error.main}
                                    />
                                  </IconButton>
                                  <Divider
                                    orientation="vertical"
                                    flexItem
                                  />
                                </Grid>
                              )}
                            </>
                          </Grid>
                        ),
                        ...(!state.auditDocument && {
                          endAdornment: (
                            <AddNewDocument
                              buttonType="custom"
                              CustomButtonContent={
                                <AttachIcon
                                  color={theme.palette.primary.main}
                                  width="35px"
                                  height="35px"
                                />
                              }
                              documentFieldsProps={{
                                legalEntity: legalEntity ?? null,
                                employee,
                                category: DOCUMENT_CATEGORIES.ACCOUNTING_INPUT as DocumentCategoryRef,
                                type: DOCUMENT_TYPES.TravelExpensesReceipt as DocumentTypeRef,
                                documentDate: new Date(),
                              }}
                              readOnlyFields={{
                                isLegalEntityRO: true,
                                isCategoryRO: true,
                                isEmployeeRO: true,
                                isTypeRO: true,
                                isDateRO: true,
                              }}
                              hideReadOnlyFields
                              closeAfterSave
                              handleNotifyParent={(document: DocumentDTO | null) => {
                                setState((state) => ({ ...state, auditDocument: document }));
                              }}
                            />
                          ),
                        }),
                      }}
                    />

                    <IconButton
                      onClick={saveRequest}
                      title={"Add message"}
                      disabled={state.loading || readOnly}
                    >
                      <SendIcon
                        color={theme.palette.primary.main}
                        width="25px"
                        height="25px"
                      />
                    </IconButton>
                  </NotesTextfieldContainer>
                )}

                <NotesMessagesContainer>
                  <ChatContainer>
                    {!state.allMessages.length && (
                      <Typography
                        variant="body2"
                        style={{ color: theme.palette.secondary.light }}
                      >
                        <i>&lt; {`${t(`${tRP}.no_audit_notes`)}`}&gt;</i>
                      </Typography>
                    )}

                    {!!state.allMessages.length && (
                      <>
                        {state.allMessages.map((item) => {
                          const sendMessageTime = `${intlFormatDateShort(
                            new Date(item.createdAt)
                          )} ${intlFormatDateToHour(new Date(item.createdAt))}`;

                          return (
                            <React.Fragment key={item.id}>
                              <MessageContainer>
                                <Grid
                                  sx={{
                                    "& p": {
                                      display: "inline",
                                      color: theme.palette.text.disabled,
                                    },
                                  }}
                                >
                                  <Typography>{item.createdByUser!.name}</Typography>
                                  &nbsp;
                                  <Typography variant="body2">
                                    {item.asAuditor
                                      ? `${t(`${tRP}.auditor`)}`
                                      : item.asManager
                                      ? `${t(`${tRP}.manager`)}`
                                      : ""}
                                  </Typography>
                                  &nbsp;
                                  <Typography variant="body2">{sendMessageTime}</Typography>
                                </Grid>

                                <RequestContainer>
                                  {item.auditDocument && (
                                    <>
                                      <AttachedDocuments
                                        documentIDs={[item.auditDocument?.id ?? ""]}
                                        count={item.auditDocument?.filesCount ?? 0}
                                        buttonSize="2x"
                                        useSimpleView
                                        documentIsReadOnly={item.travelExpenseAuditNoteReviews.length > 0}
                                      />
                                      <Divider
                                        orientation="vertical"
                                        flexItem
                                      />
                                    </>
                                  )}
                                  <Typography>{item.notes}</Typography>
                                </RequestContainer>
                              </MessageContainer>
                            </React.Fragment>
                          );
                        })}
                      </>
                    )}
                  </ChatContainer>
                </NotesMessagesContainer>
              </NotesBlockContainer>
            </NotesBlockWrapper>
          </Grid>

          {state.allMessages.length < state.totalItems && (
            <Grid sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                sx={{ textTransform: "none" }}
                onClick={() => loadNotes()}
                disabled={state.loading}
              >
                {`${t(`${tRP}.load_more`)}`}
              </Button>
            </Grid>
          )}
        </Grid>
      </LoadingOverlay>
    </Grid>
  );
};

export default React.memo(AuditNotes);
